import { AbstractControl, ControlValueAccessor, NgControl, NgModel, ValidationErrors, Validator } from '@angular/forms';
import {
    EventEmitter,
    Input,
    Output,
    QueryList,
    ViewChildren,
    Directive,
    Injector,
    ChangeDetectorRef,
} from '@angular/core';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';

/**
 * routine for generic control value accessing and validating
 * add NG_VALUE_ACCESSOR and NG_VALIDATORS providers to derived component
 */
@Directive()
export abstract class ControlValueAccessorBase<T> extends FiComponentsContainer implements ControlValueAccessor {
    @Input() ngModel: T;
    @Output() ngModelChange = new EventEmitter<T>();
    private propagateChange: (v: T) => void;
    protected onTouched: () => void = () => {};
    protected isDirty = false;
    registerOnChange(fn: (v: T) => void) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    firstWriteValue = true;
    writeValue(v: T): void {
        if (!this.firstWriteValue && v !== null) {
            this.ngModel = v;
            this.firstWriteValue = false;
        }
    }

    doPropagateChanges() {
        this.isDirty = true;
        if (this.propagateChange) {
            this.propagateChange(this.ngModel);
        } else {
            this.ngModelChange.emit(this.ngModel);
        }
    }
}

@Directive()
export abstract class ControlValueAccessorValidatorBase<T>
    extends ControlValueAccessorBase<T>
    implements ControlValueAccessor, Validator
{
    private isFirstValidation = true;
    validationErrors: ValidationErrors;

    protected controlComponent: AbstractControl;
    protected cdr: ChangeDetectorRef;

    constructor(injector: Injector) {
        super();
        this.cdr = injector.get(ChangeDetectorRef);
    }

    markForCheck() {
        this.cdr.markForCheck();
    }

    /* Don't override validate method, instead implement doValidate (method should return only errors of component)  */
    validate(c: AbstractControl): ValidationErrors {
        this.controlComponent = c;
        //let innerValidationResult: ValidationErrors = null;

        if (this.isFirstValidation) {
            this.isFirstValidation = false;
            setTimeout(() => c.updateValueAndValidity());
            return null;
        }

        let componentValidationResult: ValidationErrors = this.doValidate(c);
        const validationResult = this.coerceValidationResult(componentValidationResult);
        return validationResult;
    }

    get isTouched(): boolean {
        return this.controlComponent?.touched;
    }

    doValidate(c: AbstractControl): ValidationErrors {
        return null;
    }

    protected coerceValidationResult(validationResult: ValidationErrors): ValidationErrors {
        let result: ValidationErrors = Object.assign({}, validationResult);
        let hasErrors = false;
        for (let prop in result) {
            if (result[prop] === true) {
                hasErrors = true;
            } else {
                delete result[prop];
            }
        }
        this.validationErrors = hasErrors ? result : null;
        return this.validationErrors;
    }
}
